import { useEffect, useState } from 'react';

import { MULTIPLE_LOAN_REQUEST_STATUS } from 'enums';
import useMounted from 'hooks/useMounted';
import usePageable from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { MultipleLoanRequestVOModel } from 'models/vo/MultipleLoanRequestVO';
import { requestDealerMultipleRequestLoanList } from 'utils/http/api/dealer/multiple-request-loans';
import useModal from 'utils/modal/useModal';

export const useDealerBulkFinancingConfirmationListPageState = () => {
  const mounted = useMounted();
  const modal = useModal();

  const [dataState, setDataState] = useState({
    bulkFinancingWaitingList: {} as Pageable<MultipleLoanRequestVOModel[]>,
    bulkFinancingCompletedList: {} as Pageable<MultipleLoanRequestVOModel[]>,
  });

  const { pageable: bulkFinancingWaitingPageable, setPageable: setBulkFinancingWaitingPageable } = usePageable();
  const { pageable: bulkFinancingCompletedPageable, setPageable: setBulkFinancingCompletedPageable } = usePageable();

  useEffect(() => {
    if (mounted) {
      fetchAll();
    }

    return () => {};
  }, [mounted]);

  const fetchAll = async () => {
    const [fetchMultipleRequestLoanWaitingList, fetchMultipleRequestLoanCompletedList] = await Promise.all([
      requestDealerMultipleRequestLoanList(
        bulkFinancingWaitingPageable.currentPage,
        bulkFinancingWaitingPageable.sizePerPage,
        {
          multipleLoanRequestStatusConditions: [
            MULTIPLE_LOAN_REQUEST_STATUS.CREATED,
            MULTIPLE_LOAN_REQUEST_STATUS.APPLIED,
          ],
        },
      ),
      requestDealerMultipleRequestLoanList(
        bulkFinancingCompletedPageable.currentPage,
        bulkFinancingCompletedPageable.sizePerPage,
        {
          multipleLoanRequestStatusConditions: [
            MULTIPLE_LOAN_REQUEST_STATUS.APPROVED,
            MULTIPLE_LOAN_REQUEST_STATUS.REJECTED,
            MULTIPLE_LOAN_REQUEST_STATUS.CANCELLED,
          ],
        },
      ),
    ]);

    setDataState(prevState => ({
      ...prevState,
      bulkFinancingWaitingList: fetchMultipleRequestLoanWaitingList,
      bulkFinancingCompletedList: fetchMultipleRequestLoanCompletedList,
    }));
    setBulkFinancingWaitingPageable(fetchMultipleRequestLoanWaitingList);
    setBulkFinancingCompletedPageable(fetchMultipleRequestLoanCompletedList);
  };

  const multipleRequestLoanWaitingListPaginate = async (page: number, sizePerPage: number) => {
    try {
      const response = await requestDealerMultipleRequestLoanList(page, sizePerPage, {
        multipleLoanRequestStatusConditions: [
          MULTIPLE_LOAN_REQUEST_STATUS.CREATED,
          MULTIPLE_LOAN_REQUEST_STATUS.APPLIED,
        ],
      });
      setDataState(prevState => ({
        ...prevState,
        bulkFinancingWaitingList: response,
      }));
      setBulkFinancingWaitingPageable(response);
    } catch (e) {
      modal.show(e);
    }
  };

  const multipleRequestLoanCompletedListPaginate = async (page: number, sizePerPage: number) => {
    try {
      const response = await requestDealerMultipleRequestLoanList(page, sizePerPage, {
        multipleLoanRequestStatusConditions: [
          MULTIPLE_LOAN_REQUEST_STATUS.APPROVED,
          MULTIPLE_LOAN_REQUEST_STATUS.REJECTED,
          MULTIPLE_LOAN_REQUEST_STATUS.CANCELLED,
        ],
      });
      setDataState(prevState => ({
        ...prevState,
        bulkFinancingCompletedList: response,
      }));
      setBulkFinancingCompletedPageable(response);
    } catch (e) {
      modal.show(e);
    }
  };

  return {
    waitingTabUtils: {
      bulkFinancingWaitingList: dataState.bulkFinancingWaitingList,
      bulkFinancingWaitingPageable,
      multipleRequestLoanWaitingListPaginate,
    },
    completedTabUtils: {
      bulkFinancingCompletedList: dataState.bulkFinancingCompletedList,
      bulkFinancingCompletedPageable,
      multipleRequestLoanCompletedListPaginate,
    },
  };
};
